import { gql } from '@apollo/client';
// Authenticator TOTP mutations
export const REQUEST_SETUP_MFA_AUTHENTICATOR_TOTP = gql`
  mutation RequestSetupMfaAuthenticatorTotpForMe {
    requestSetupMfaAuthenticatorTotpForMe {
      mfaAuthenticatorTotpProvisionRequest {
        id
        insertedAt
        updatedAt
        totpSecretKey
      }
    }
  }
`;

export const CONFIRM_MFA_AUTHENTICATOR_TOTP = gql`
  mutation ConfirmMfaAuthenticatorTotpProvisionRequest(
    $id: ID!
    $input: ConfirmMfaAuthenticatorTotpProvisionRequestInput!
  ) {
    confirmMfaAuthenticatorTotpProvisionRequest(id: $id, input: $input) {
      user {
        id
        email
      }
    }
  }
`;

export const DISABLE_MFA_AUTHENTICATOR_TOTP = gql`
  mutation DisableMyMfaAuthenticatorTotp($input: DisableMyMfaAuthenticatorTotpInput!) {
    disableMyMfaAuthenticatorTotp(input: $input) {
      user {
        id
        email
      }
    }
  }
`;

// Email TOTP mutations
export const REQUEST_SETUP_MFA_EMAIL_TOTP = gql`
  mutation RequestSetupMfaEmailTotpForMe {
    requestSetupMfaEmailTotpForMe {
      mfaEmailTotpProvisionRequest {
        id
        insertedAt
        updatedAt
      }
    }
  }
`;

export const CONFIRM_MFA_EMAIL_TOTP = gql`
  mutation ConfirmMfaEmailTotpProvisionRequest(
    $id: ID!
    $input: ConfirmMfaEmailTotpProvisionRequestInput!
  ) {
    confirmMfaEmailTotpProvisionRequest(id: $id, input: $input) {
      user {
        id
        email
      }
    }
  }
`;

export const DISABLE_MFA_EMAIL_TOTP = gql`
  mutation DisableMyMfaEmailTotp($input: DisableMyMfaEmailTotpInput!) {
    disableMyMfaEmailTotp(input: $input) {
      user {
        id
        email
      }
    }
  }
`;

// SMS TOTP mutations
export const REQUEST_SETUP_MFA_SMS_TOTP = gql`
  mutation RequestSetupMfaSmsTotpForMe($input: RequestSetupMfaSmsTotpForMeInput!) {
    requestSetupMfaSmsTotpForMe(input: $input) {
      mfaSmsTotpProvisionRequest {
        id
        insertedAt
        updatedAt
      }
    }
  }
`;

export const CONFIRM_MFA_SMS_TOTP = gql`
  mutation ConfirmMfaSmsTotpProvisionRequest(
    $id: ID!
    $input: ConfirmMfaSmsTotpProvisionRequestInput!
  ) {
    confirmMfaSmsTotpProvisionRequest(id: $id, input: $input) {
      user {
        id
        email
      }
    }
  }
`;

export const DISABLE_MFA_SMS_TOTP = gql`
  mutation DisableMyMfaSmsTotp($input: DisableMyMfaSmsTotpInput!) {
    disableMyMfaSmsTotp(input: $input) {
      user {
        id
        email
      }
    }
  }
`;

// Request verification code for already-enabled email MFA
export const REQUEST_MFA_EMAIL_TOTP = gql`
  mutation RequestMfaEmailTotpForMe {
    requestMfaEmailTotpForMe {
      status
    }
  }
`;

// Request verification code for already-enabled SMS MFA
export const REQUEST_MFA_SMS_TOTP = gql`
  mutation RequestMfaSmsTotpForMe {
    requestMfaSmsTotpForMe {
      status
    }
  }
`;

// Backup tokens mutation
export const REFRESH_MFA_BACKUP_TOKENS = gql`
  mutation RefreshMfaBackupTokensForMe {
    refreshMfaBackupTokensForMe {
      mfaBackupTokens {
        token
      }
    }
  }
`;

// Types for mutation responses
export interface MfaAuthenticatorTotpProvisionRequest {
  id: string;
  insertedAt: string;
  updatedAt: string;
  totpSecretKey: string;
}

export interface MfaEmailTotpProvisionRequest {
  id: string;
  insertedAt: string;
  updatedAt: string;
}

export interface MfaSmsTotpProvisionRequest {
  id: string;
  insertedAt: string;
  updatedAt: string;
}

export interface RequestSetupMfaAuthenticatorTotpResponse {
  requestSetupMfaAuthenticatorTotpForMe: {
    mfaAuthenticatorTotpProvisionRequest: MfaAuthenticatorTotpProvisionRequest;
  };
}

export interface RequestSetupMfaEmailTotpResponse {
  requestSetupMfaEmailTotpForMe: {
    mfaEmailTotpProvisionRequest: MfaEmailTotpProvisionRequest;
  };
}

export interface RequestSetupMfaSmsTotpResponse {
  requestSetupMfaSmsTotpForMe: {
    mfaSmsTotpProvisionRequest: MfaSmsTotpProvisionRequest;
  };
}

export interface RequestMfaEmailTotpResponse {
  requestMfaEmailTotpForMe: {
    status: 'OK' | 'UNAVAILABLE';
  };
}

export interface RequestMfaSmsTotpResponse {
  requestMfaSmsTotpForMe: {
    status: 'OK' | 'UNAVAILABLE';
  };
}

export interface UserMfaBackupToken {
  token: string;
}

export interface RefreshMfaBackupTokensResponse {
  refreshMfaBackupTokensForMe: {
    mfaBackupTokens: UserMfaBackupToken[];
  };
}

export type MfaMethodType = 'authenticator' | 'email' | 'sms';

export interface MfaMethodConfig {
  type: MfaMethodType;
  label: string;
  description: string;
  icon: string;
  codeLength: number;
}

export const MFA_METHOD_CONFIGS: Record<MfaMethodType, MfaMethodConfig> = {
  authenticator: {
    type: 'authenticator',
    label: 'Authenticator App',
    description: 'Use an authenticator app like Google Authenticator or Authy.',
    icon: 'smartphone',
    codeLength: 6,
  },
  email: {
    type: 'email',
    label: 'Email Verification',
    description: 'Receive a verification code via email.',
    icon: 'mail',
    codeLength: 8,
  },
  sms: {
    type: 'sms',
    label: 'SMS Verification',
    description:
      'Receive a verification code via text message. Only US and Canadian numbers are supported.',
    icon: 'sms',
    codeLength: 6,
  },
};
